
import axios from "axios";
import store from "../store/index";
import router from "../router/index";

import {
    ElMessage, ElLoading
} from 'element-plus';

/**
 * 说明：本订单同时对接「老管理系统」和「新 Python 服务」两个后端。
 * 为保证两者互不影响，这里用工厂 createHttpClient(baseURL) 创建相互隔离的 axios 实例，
 * 各自拥有独立 baseURL，但复用同一套拦截器逻辑（loading / token / 错误处理）。
 *
 * - 老系统：默认实例 mainClient，对应方法 getRequest/postRequest/...（保持原有用法不变）
 * - Python：pyClient，对应方法 pyGetRequest/pyPostRequest/...
 */

// ===== 全局共享的 loading 与鉴权状态（跨实例共用，保证全局只有一个 loading 遮罩）=====
let loadingInstance = null;
let requestCount = 0;
const LOADING_COOLDOWN = 500;
const LOADING_DELAY = 200;
const NAV_SUPPRESS_DURATION = 800; // 路由切换后抑制全局 loading 的时长，给新页面 hideLoading 请求留出时间窗口
let lastLoadingTime = 0;
let loadingDelayTimer = null;
let navSuppressTimer = null; // 路由切换后的抑制定时器
let isAuthRedirecting = false;
let lastAuthExpiredMessageTime = 0;
const AUTH_EXPIRED_MESSAGE_COOLDOWN = 3000;
const pendingGetRequests = new Map();

function isStaleAuthResponse(requestToken) {
    const currentToken = store.state.token || '';
    const tokenAtRequest = requestToken || '';
    return Boolean(currentToken && tokenAtRequest !== currentToken);
}

function stableStringify(value) {
    if (Array.isArray(value)) {
        return `[${value.map(item => stableStringify(item)).join(',')}]`;
    }
    if (value && typeof value === 'object') {
        return `{${Object.keys(value).sort().map(key => `${key}:${stableStringify(value[key])}`).join(',')}}`;
    }
    return JSON.stringify(value ?? null);
}

function buildGetDedupeKey(client, url, params, config) {
    return [
        client.defaults?.baseURL || '',
        url,
        stableStringify(params || {}),
        config.hideLoading ? 'hideLoading' : 'showLoading',
        config.suppressErrorMessage ? 'suppressError' : 'showError',
        config.responseType || '',
        stableStringify(config.headers || {}),
        config.withCredentials ? 'withCredentials' : '',
        store.state.token || ''
    ].join('|');
}

function handleAuthExpired(message = '登录已过期，请重新登录', requestToken = '') {
    if (isStaleAuthResponse(requestToken)) {
        return false;
    }

    const currentPath = router.currentRoute?.value?.path || '';
    if (currentPath === '/login') {
        return false;
    }

    // 鉴权失效时必须同时清理用户和菜单缓存，不能只删除 token，
    // 否则重新登录或刷新时会短暂甚至持续显示上一个账号的权限菜单。
    store.commit('clearStore');

    const now = Date.now();
    if (now - lastAuthExpiredMessageTime > AUTH_EXPIRED_MESSAGE_COOLDOWN) {
        ElMessage.error(message);
        lastAuthExpiredMessageTime = now;
    }

    if (!isAuthRedirecting) {
        isAuthRedirecting = true;
        router.push('/login').finally(() => {
            setTimeout(() => {
                isAuthRedirecting = false;
            }, 300);
        });
    }
    return true;
}

// 显示loading（延迟 LOADING_DELAY ms 后才真正显示，避免快速请求闪烁）
function showLoading() {
    // 路由切换后的抑制期内，不显示全局 loading，让新页面的 hideLoading 请求安静完成
    if (navSuppressTimer) {
        requestCount++;
        return false;
    }

    if (requestCount === 0) {
        const now = Date.now();
        if (now - lastLoadingTime < LOADING_COOLDOWN) {
            requestCount++;
            return false;
        }

        if (!loadingDelayTimer) {
            loadingDelayTimer = setTimeout(() => {
                loadingDelayTimer = null;
                if (requestCount > 0 && !loadingInstance) {
                    loadingInstance = ElLoading.service({
                        lock: true,
                        text: '加载中...',
                        background: 'rgba(0, 0, 0, 0.7)',
                    });
                    lastLoadingTime = Date.now();
                }
            }, LOADING_DELAY);
        }
    }
    requestCount++;
    return true;
}

// 隐藏loading
function hideLoading() {
    if (requestCount <= 0) {
        return;
    }

    requestCount--;
    if (requestCount === 0) {
        if (loadingDelayTimer) {
            clearTimeout(loadingDelayTimer);
            loadingDelayTimer = null;
        }
        if (loadingInstance) {
            loadingInstance.close();
            loadingInstance = null;
        }
        lastLoadingTime = Date.now();
    }
}

// 路由切换时强制清理残留的全局 loading，并启动抑制期，防止新页面的主请求再次弹出全局遮罩
function forceHideLoading() {
    if (loadingDelayTimer) {
        clearTimeout(loadingDelayTimer);
        loadingDelayTimer = null;
    }
    if (loadingInstance) {
        loadingInstance.close();
        loadingInstance = null;
    }
    requestCount = 0;
    // 启动抑制期：800ms 内新请求不会触发全局 loading，给页面的 hideLoading 请求留出完成时间
    if (navSuppressTimer) {
        clearTimeout(navSuppressTimer);
    }
    navSuppressTimer = setTimeout(() => {
        navSuppressTimer = null;
    }, NAV_SUPPRESS_DURATION);
}

/**
 * 创建一个带统一拦截器的 axios 实例。
 * @param {string} baseURL 该实例的基础地址
 * @returns {import('axios').AxiosInstance}
 */
function createHttpClient(baseURL) {
    const instance = axios.create({
        baseURL,
        timeout: 300000,
    });

    // 请求拦截器
    instance.interceptors.request.use(
        config => {
            // 显示loading（可以通过config.hideLoading来控制是否显示）
            if (!config.hideLoading) {
                config.__loadingShown = showLoading();
            }

            // 在发送请求之前做些什么
            // 登录、验证码等公开接口可通过 skipAuth 显式禁止携带历史 token。
            const authToken = config.skipAuth ? '' : (store.state.token || '');
            config.__authToken = authToken;
            if (authToken) {
                config.headers = config.headers || {};
                config.headers['Authorization'] = authToken;
            }
            return config;
        },
        error => {
            // 对请求错误做些什么
            if (error && error.config && error.config.__loadingShown) {
                hideLoading();
            }
            console.error('请求错误：', error);
            return Promise.reject(error);
        }
    );

    // 响应拦截器
    instance.interceptors.response.use(
        response => {
            // 隐藏loading
            if (response.config && response.config.__loadingShown) {
                hideLoading();
            }

            // 对响应数据做点什么
            const res = response.data;

            // 根据返回的状态码做相应处理
            if (res.code === 200 || res.code === 0 || res.success) {
                return res;
            } else if (res.code === 401) {
                if (response.config?.skipAuth) {
                    if (!response.config?.suppressErrorMessage) {
                        ElMessage.error(res.message || '请求失败');
                    }
                    return Promise.reject(new Error(res.message || '请求失败'));
                }
                // token过期或未登录
                handleAuthExpired('登录已过期，请重新登录', response.config?.__authToken);
                return Promise.reject(new Error(res.message || '未授权'));
            } else {
                if (!response.config?.suppressErrorMessage) {
                    ElMessage.error(res.message || '请求失败');
                }
                return Promise.reject(new Error(res.message || '请求失败'));
            }
        },
        error => {
            // 隐藏loading
            if (error && error.config && error.config.__loadingShown) {
                hideLoading();
            }

            // 对响应错误做点什么
            console.error('响应错误：', error);
            const suppressErrorMessage = !!(error?.config?.suppressErrorMessage || error?.response?.config?.suppressErrorMessage);

            if (error.response) {
                const responseData = error.response.data || {};
                const backendMessage = responseData.message || responseData.msg || responseData.detail || '';
                let notified = false;
                switch (error.response.status) {
                    case 401:
                        if (error?.config?.skipAuth || error?.response?.config?.skipAuth) {
                            if (!suppressErrorMessage) {
                                ElMessage.error(backendMessage || '请求失败');
                                notified = true;
                            }
                        } else {
                            notified = handleAuthExpired('未授权，请重新登录', error?.config?.__authToken || error?.response?.config?.__authToken);
                        }
                        break;
                    case 403:
                        if (!suppressErrorMessage) {
                            ElMessage.error(backendMessage || '拒绝访问');
                            notified = true;
                        }
                        break;
                    case 404:
                        if (!suppressErrorMessage) {
                            ElMessage.error(backendMessage || '请求地址不存在');
                            notified = true;
                        }
                        break;
                    case 500:
                        if (!suppressErrorMessage) {
                            ElMessage.error(backendMessage || '服务器内部错误');
                            notified = true;
                        }
                        break;
                    default:
                        if (!suppressErrorMessage) {
                            ElMessage.error(backendMessage || '请求失败');
                            notified = true;
                        }
                }
                if (notified) {
                    error.__notified = true;
                }
                if (backendMessage) {
                    error.message = backendMessage;
                }
            } else if (error.message) {
                if (error.message.includes('timeout')) {
                    if (!suppressErrorMessage) {
                        ElMessage.error('请求超时，请稍后重试');
                    }
                } else if (error.message.includes('Network Error')) {
                    if (!suppressErrorMessage) {
                        ElMessage.error('网络错误，请检查网络连接');
                    }
                } else {
                    if (!suppressErrorMessage) {
                        ElMessage.error(error.message);
                    }
                }
            }

            return Promise.reject(error);
        }
    );

    return instance;
}

/**
 * 基于某个 axios 实例，生成一组 GET/POST/PUT/DELETE 请求方法。
 * @param {import('axios').AxiosInstance} client
 */
function buildRequestMethods(client) {
    function getRequest(url, params = {}, config = {}) {
        if (params && params.hideLoading !== undefined && config.hideLoading === undefined) {
            config.hideLoading = params.hideLoading;
            const { hideLoading, ...rest } = params;
            params = rest;
        }
        const dedupeEnabled = config.dedupe !== false;
        const requestConfig = {
            params,
            ...config
        };
        delete requestConfig.dedupe;
        const dedupeKey = dedupeEnabled ? buildGetDedupeKey(client, url, params, requestConfig) : '';
        if (dedupeKey && pendingGetRequests.has(dedupeKey)) {
            return pendingGetRequests.get(dedupeKey);
        }
        const requestPromise = client
            .get(url, requestConfig)
            .finally(() => {
                if (dedupeKey) {
                    pendingGetRequests.delete(dedupeKey);
                }
            });
        if (dedupeKey) {
            pendingGetRequests.set(dedupeKey, requestPromise);
        }
        return requestPromise;
    }

    function postRequest(url, data = {}, config = {}) {
        if (data && data.hideLoading !== undefined && config.hideLoading === undefined) {
            config.hideLoading = data.hideLoading;
            const { hideLoading, ...rest } = data;
            data = rest;
        }
        return new Promise((resolve, reject) => {
            client
                .post(url, data, config)
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    function putRequest(url, data = {}, config = {}) {
        if (data && data.hideLoading !== undefined && config.hideLoading === undefined) {
            config.hideLoading = data.hideLoading;
            const { hideLoading, ...rest } = data;
            data = rest;
        }
        return new Promise((resolve, reject) => {
            client
                .put(url, data, config)
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    function deleteRequest(url, params = {}, config = {}) {
        if (params && params.hideLoading !== undefined && config.hideLoading === undefined) {
            config.hideLoading = params.hideLoading;
            const { hideLoading, ...rest } = params;
            params = rest;
        }
        return new Promise((resolve, reject) => {
            client
                .delete(url, {
                    params,
                    ...config
                })
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    return { getRequest, postRequest, putRequest, deleteRequest };
}

// ===== 老管理系统实例（保持原有 baseURL 与用法）=====
const mainClient = createHttpClient(process.env.VUE_APP_BASE_API);
const mainMethods = buildRequestMethods(mainClient);

// ===== Python 服务实例（新业务）=====
const pyClient = createHttpClient(process.env.VUE_APP_PY_API);
const pyMethods = buildRequestMethods(pyClient);

export { forceHideLoading };

export default {
    // —— 老系统（保持向后兼容，现有调用无需改动）——
    getRequest: mainMethods.getRequest,
    postRequest: mainMethods.postRequest,
    putRequest: mainMethods.putRequest,
    deleteRequest: mainMethods.deleteRequest,

    // —— Python 服务 ——
    pyGetRequest: pyMethods.getRequest,
    pyPostRequest: pyMethods.postRequest,
    pyPutRequest: pyMethods.putRequest,
    pyDeleteRequest: pyMethods.deleteRequest,

    // 直接暴露两个实例，方便特殊情况使用
    axios: mainClient,
    mainClient,
    pyClient,
};
